import { useMemo, useState } from "react";
import MenuIcon from "@mui/icons-material/Menu";
import { MuiMaterials } from "../../@index.js";
import { getSiteWindowMenuItems, openSiteWindow } from "./siteWindowNav.js";
import { getWindowChromeThemeColor } from "./windowChromeTheme.js";
import { withScrollHintMenuProps } from "./ScrollHintPaper.js";

/**
 * Clickable window title that lists the other Site XXXX tools. Selecting an item
 * dispatches openSiteWindow so ProtectedRoute can open/navigate while preserving siteGuid.
 */
export default function SiteWindowTitle({
    title,
    subtitle = null,
    siteWindowId,
    siteGuid = null,
    textSecondary: textSecondaryProp = null,
    titleId = undefined,
    /** When true, title does not grow to fill the header (toolbar / inline use). */
    compact = false,
    /**
     * Visual size of the nav button.
     * - "default": current header title size (1.25rem)
     * - "small": slightly smaller, for use beside SiteHeaderSiteSelect
     */
    size = "default",
    /** When true, render a hamburger IconButton instead of the title text. */
    iconMenu = false,
}) {
    const [anchorEl, setAnchorEl] = useState(null);
    const textSecondary =
        textSecondaryProp ?? getWindowChromeThemeColor("#666666", "#c4a574");
    const menuItems = useMemo(() => getSiteWindowMenuItems(), [anchorEl]);
    const flexSx = compact || iconMenu ? { flex: "0 0 auto", maxWidth: "100%" } : { flex: 1 };
    const fontSize = size === "small" ? "0.95rem" : (compact ? "1.1rem" : "1.25rem");

    if (!siteWindowId) {
        return (
            <MuiMaterials.Typography
                variant="h6"
                sx={{ ...flexSx, fontWeight: 700, minWidth: 0, fontSize }}
                id={titleId}
            >
                {title}
                {subtitle ? (
                    <MuiMaterials.Typography
                        component="span"
                        variant="body2"
                        sx={{ ml: 1, color: textSecondary }}
                    >
                        {subtitle}
                    </MuiMaterials.Typography>
                ) : null}
            </MuiMaterials.Typography>
        );
    }

    const open = Boolean(anchorEl);
    const menuText = getWindowChromeThemeColor("#333333", "#f4cc8e");
    const menuBg = getWindowChromeThemeColor("#ffffff", "#2d2d2d");
    const menuHover = getWindowChromeThemeColor("#f5f5f5", "#3d3d3d");
    const menuSelected = getWindowChromeThemeColor("#e8f0fe", "#3d3d3d");

    const trigger = iconMenu ? (
        <MuiMaterials.IconButton
            type="button"
            onClick={(e) => {
                e.stopPropagation();
                setAnchorEl(e.currentTarget);
            }}
            aria-label={title || "Site tools"}
            aria-haspopup="menu"
            aria-expanded={open ? "true" : undefined}
            aria-controls={open ? "site-window-nav-menu" : undefined}
            id={titleId}
            sx={{
                color: "inherit",
                p: 0.75,
                "&:hover": {
                    backgroundColor: getWindowChromeThemeColor(
                        "rgba(0,0,0,0.04)",
                        "rgba(255,255,255,0.06)",
                    ),
                },
            }}
        >
            <MenuIcon />
        </MuiMaterials.IconButton>
    ) : (
        <MuiMaterials.Button
            type="button"
            onClick={(e) => {
                e.stopPropagation();
                setAnchorEl(e.currentTarget);
            }}
            aria-haspopup="menu"
            aria-expanded={open ? "true" : undefined}
            aria-controls={open ? "site-window-nav-menu" : undefined}
            sx={{
                display: "inline-flex",
                alignItems: "center",
                textTransform: "none",
                fontWeight: 700,
                fontSize,
                lineHeight: 1.6,
                px: 0.5,
                py: 0,
                minWidth: 0,
                minHeight: "2.1rem",
                height: "2.1rem",
                mt: size === "small" ? "3px" : 0,
                ml: 0,
                color: size === "small" ? textSecondary : "inherit",
                backgroundColor: "transparent",
                "&:hover": {
                    backgroundColor: getWindowChromeThemeColor(
                        "rgba(0,0,0,0.04)",
                        "rgba(255,255,255,0.06)",
                    ),
                },
            }}
        >
            <span id={titleId}>{title}</span>
        </MuiMaterials.Button>
    );

    return (
        <>
            <MuiMaterials.Box
                sx={{
                    ...flexSx,
                    minWidth: 0,
                    display: "flex",
                    alignItems: "center",
                    flexWrap: "nowrap",
                    gap: 0.5,
                    alignSelf: "center",
                }}
            >
                {trigger}
                {!iconMenu && subtitle ? (
                    <MuiMaterials.Typography
                        component="span"
                        variant="body2"
                        sx={{ color: textSecondary, whiteSpace: "nowrap" }}
                    >
                        {subtitle}
                    </MuiMaterials.Typography>
                ) : null}
            </MuiMaterials.Box>
            <MuiMaterials.Menu
                id="site-window-nav-menu"
                anchorEl={anchorEl}
                open={open}
                onClose={() => setAnchorEl(null)}
                style={{ zIndex: 1600 }}
                sx={{ zIndex: 1600 }}
                anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
                transformOrigin={{ vertical: "top", horizontal: "left" }}
                {...withScrollHintMenuProps({
                    PaperProps: {
                        sx: {
                            backgroundColor: menuBg,
                            backgroundImage: "none",
                            color: menuText,
                            minWidth: 220,
                            maxWidth: "min(90vw, 480px)",
                        },
                    },
                    MenuListProps: {
                        sx: { backgroundColor: menuBg },
                    },
                })}
            >
                {menuItems.map((item) => {
                    const isCurrent = item.id === siteWindowId;
                    return (
                        <MuiMaterials.MenuItem
                            key={item.id}
                            selected={isCurrent}
                            disabled={isCurrent}
                            onClick={() => {
                                setAnchorEl(null);
                                if (!isCurrent) openSiteWindow(item.id, siteGuid);
                            }}
                            sx={{
                                color: `${menuText} !important`,
                                backgroundColor: "transparent",
                                "&.Mui-selected": {
                                    backgroundColor: `${menuSelected} !important`,
                                    color: `${menuText} !important`,
                                },
                                "&.Mui-disabled": {
                                    color: `${menuText} !important`,
                                    opacity: 0.55,
                                },
                                "&:hover": {
                                    backgroundColor: `${menuHover} !important`,
                                    color: `${menuText} !important`,
                                },
                            }}
                        >
                            {item.label}
                        </MuiMaterials.MenuItem>
                    );
                })}
            </MuiMaterials.Menu>
        </>
    );
}
